// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextField,
} from '@mui/material';
import { DateTime, Zone } from 'luxon';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Determines if the option is equal to the value.
 *
 * @param option - A timezone option to compare.
 * @param value - A timezone value to compare.
 */
function isOptionEqualToValue(option: Zone, value: Zone) {
  return option.equals(value);
}

/**
 * Returns the unique key of the option.
 *
 * @param option - A timezone option to get the key from.
 */
function getOptionKey(option: Zone) {
  return option.name;
}

interface Props<
  Option extends Zone,
  DisableClearable extends boolean | undefined,
> extends Omit<
    AutocompleteProps<Option, false, DisableClearable, false>,
    'renderInput' | 'getOptionKey' | 'getOptionLabel' | 'isOptionEqualToValue'
  > {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;

  /**
   * If provided, the reference date and time will be used to determine the
   * option names. Otherwise, the current date and time will be used.
   */
  reference?: DateTime;
}

/**
 * Renders a timezone autocomplete component and forwards a ref to the input
 * element.
 */
function TimezoneAutocompleteRenderer<
  Option extends Zone,
  DisableClearable extends boolean | undefined,
>(props: Props<Option, DisableClearable>, ref: ForwardedRef<HTMLInputElement>) {
  const { name, label, placeholder, error, helperText, ...autocompleteProps } =
    props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  const getOptionLabel = (option: Zone) => {
    const dateTime = props.reference ?? DateTime.local();
    const milliseconds = dateTime.toMillis();
    const offsetName = option.offsetName(milliseconds, { format: 'long' });

    return `${option.name} (${offsetName})`;
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      renderInput={renderInput}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}

/**
 * An autocomplete component for selecting a timezone from a list of options.
 */
export const TimezoneAutocomplete = forwardRef(TimezoneAutocompleteRenderer);
